import { z } from 'zod';
import { GitHostKind, GitRepoVisibility } from './git.js';
/**
 * Gắn cả nhóm kho — APM-GIT-s, docs/plan-git-integration.md §16.
 *
 * Một **nhóm** là nhóm GitLab (kể cả nhóm con, gắn riêng) hoặc organization GitHub.
 * Gắn nhóm = gắn ngay mọi kho **trực tiếp**, không archived của nó (≤
 * `GIT_GROUP_LINK_MAX`); kho tạo sau chỉ thành **gợi ý**. Danh sách kho của nhóm
 * không lưu — trạng thái của từng kho tính lúc đọc (§16.3).
 */
/** Trần số kho **gắn** trong một lần gắn nhóm / một lượt áp lựa chọn (§16.4). */
export const GIT_GROUP_LINK_MAX = 50;
/** Trần số mục của một lượt áp lựa chọn (§16.4). */
export const GIT_SELECTION_MAX = 500;
/** Trần trang khi liệt kê kho của một nhóm ở nguồn — 100 kho/trang (§16.4). */
export const GIT_GROUP_SOURCE_MAX_PAGES = 10;
/** Một nhóm / organization như máy chủ git mô tả. */
export const NormalizedRepoGroup = z.object({
    /** Id số của nhóm GitLab / organization GitHub, dạng chuỗi. */
    externalId: z.string(),
    /** `nhom/con` · `ten-org`. */
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
});
/** `GET …/connections/:connectionId/remote-groups` — nhóm token thấy. */
export const GitRemoteGroupsQuery = z.object({
    search: z.string().trim().max(100).optional(),
    cursor: z.string().max(2000).optional(),
});
export const GitRemoteGroup = NormalizedRepoGroup.extend({
    /** Nhóm này đã gắn vào **dự án đang xem** rồi. */
    linked: z.boolean(),
});
export const GitRemoteGroupPage = z.object({
    items: z.array(GitRemoteGroup),
    nextCursor: z.string().nullable(),
});
/**
 * `POST …/git/groups` — gắn một nhóm, **đúng một** trong hai cách: dán link (`url`)
 * hoặc đường dẫn / chọn từ danh sách (`fullPath`, kèm `externalId` nếu có). Luôn
 * cần `connectionId`: link phải thuộc máy chủ của kết nối ấy.
 */
export const LinkGitRepositoryGroupInput = z
    .object({
    connectionId: z.uuid(),
    url: z.string().trim().min(1).max(1000).optional(),
    fullPath: z.string().trim().min(1).max(255).optional(),
    externalId: z.string().trim().min(1).max(64).optional(),
})
    .superRefine((value, ctx) => {
    if ((value.url === undefined) === (value.fullPath === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['url'],
            message: 'Dán link nhóm hoặc chọn một nhóm trong danh sách',
        });
    }
});
/** Nhóm đang gắn — `GET …/git/groups`. Không gọi máy chủ git. */
export const GitRepositoryGroupView = z.object({
    id: z.uuid(),
    connectionId: z.uuid(),
    connectionLabel: z.string(),
    hostId: z.uuid(),
    hostKind: GitHostKind,
    hostName: z.string(),
    externalId: z.string(),
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
    /** Mốc gắn (gắn lại thì đặt lại) — kho tạo sau mốc này là "kho mới". */
    linkedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    /** Số kho **đang gắn** qua nhóm này. */
    linkedCount: z.number().int().nonnegative(),
    /** Số kho PM đã bỏ qua trong nhóm. */
    skippedCount: z.number().int().nonnegative(),
});
/** Một kho trong phản hồi gắn nhóm. */
export const GitGroupRepositoryBrief = z.object({
    externalId: z.string(),
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
});
/** `POST …/git/groups` (201). */
export const GitRepositoryGroupLinkResult = z.object({
    group: GitRepositoryGroupView,
    /** Kho vừa gắn (mới, hoặc khôi phục hàng cũ). */
    linked: z.array(GitGroupRepositoryBrief),
    /** Vượt `GIT_GROUP_LINK_MAX` ⇒ chưa gắn, để thành gợi ý. */
    suggested: z.array(GitGroupRepositoryBrief),
    /** Token thấy kho nhưng không đọc được MR / commit ⇒ không gắn. */
    unreadable: z.array(GitGroupRepositoryBrief),
    /** Kho của nhóm đã gắn sẵn (tay hoặc qua nhóm), không gắn trùng. */
    alreadyLinked: z.number().int().nonnegative(),
    /** Nhóm còn kho sau trần `GIT_GROUP_SOURCE_MAX_PAGES` trang — chưa liệt kê hết. */
    truncated: z.boolean(),
});
/**
 * Trạng thái một kho trong danh sách quản lý (§16.3), xét theo thứ tự:
 * `linked` đã gắn · `unlinked` đã gỡ (hàng cũ còn, không tự gợi ý lại) · `skipped`
 * PM bỏ qua · `suggested` kho ở nguồn chưa ai quyết.
 */
export const GitRepositorySelectionStatus = z.enum(['linked', 'suggested', 'skipped', 'unlinked']);
export const GitRepositorySelectionItem = z.object({
    externalId: z.string(),
    fullPath: z.string(),
    name: z.string(),
    webUrl: z.string(),
    visibility: GitRepoVisibility.nullable(),
    status: GitRepositorySelectionStatus,
    /** Hàng `git_repository` (đang gắn hoặc đã gỡ); `null` = chưa từng gắn. */
    repositoryId: z.uuid().nullable(),
    /** Đã từng gắn thì gắn **tay** hay **qua nhóm**; `null` = chưa từng gắn. */
    linkedVia: z.enum(['manual', 'group']).nullable(),
    /** Nhóm đã gắn kho này (`linkedVia = 'group'`). */
    groupId: z.uuid().nullable(),
    /** Ngày tạo kho ở nguồn; `null` = không biết (kho gắn tay ngoài mọi nhóm). */
    createdAtExt: z.iso.datetime().nullable(),
});
export const GitRepositorySelectionGroup = z.object({
    group: GitRepositoryGroupView,
    items: z.array(GitRepositorySelectionItem),
    /** Nhóm còn kho chưa liệt kê (trần trang). */
    truncated: z.boolean(),
    /**
     * Máy chủ git không trả lời / token hỏng ⇒ `items` chỉ còn kho đã gắn qua nhóm
     * (đọc từ DB), không có gợi ý.
     */
    error: z.object({ code: z.string(), message: z.string() }).nullable(),
});
/** `GET …/git/repositories/selection` — danh sách quản lý kho của dự án (§16.3). */
export const GitRepositorySelection = z.object({
    groups: z.array(GitRepositorySelectionGroup),
    /** Kho gắn tay (và kho gắn qua nhóm đã gỡ) — không nằm trong nguồn của nhóm đang gắn nào. */
    manual: z.array(GitRepositorySelectionItem),
});
/**
 * `PUT …/git/repositories/selection` — áp một tập lựa chọn. **Chỉ mục được nêu**
 * mới đổi: kho bộ dò vừa thấy sau khi PM mở màn không bị coi là "bỏ chọn".
 */
export const ApplyGitRepositorySelectionInput = z
    .object({
    groups: z
        .array(z.object({
        groupId: z.uuid(),
        repositories: z.array(z.object({ externalId: z.string().trim().min(1).max(64), selected: z.boolean() })),
    }))
        .default([]),
    manual: z.array(z.object({ repositoryId: z.uuid(), selected: z.boolean() })).default([]),
})
    .superRefine((value, ctx) => {
    const count = value.manual.length + value.groups.reduce((sum, group) => sum + group.repositories.length, 0);
    if (count > GIT_SELECTION_MAX) {
        ctx.addIssue({
            code: 'custom',
            path: ['groups'],
            message: `Tối đa ${GIT_SELECTION_MAX} kho một lần lưu`,
        });
    }
});
export const GitRepositorySelectionResult = z.object({
    /** Gắn mới (chưa từng có hàng). */
    linked: z.number().int().nonnegative(),
    /** Gắn lại kho đã gỡ — khôi phục hàng cũ, liên kết cũ còn nguyên. */
    relinked: z.number().int().nonnegative(),
    unlinked: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Bỏ dấu "bỏ qua" (kèm gắn). */
    unskipped: z.number().int().nonnegative(),
    /** Muốn gắn nhưng vượt `GIT_GROUP_LINK_MAX` — giữ nguyên trạng thái cũ, lưu lần nữa để gắn tiếp. */
    deferred: z.array(z.string()),
    /** Kho token không đọc được — không gắn. */
    unreadable: z.array(z.string()),
    selection: GitRepositorySelection,
});
//# sourceMappingURL=git-groups.js.map